import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getQueryString } from '../../helpers/url';
import { WelletContext } from '../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { errorText } from '../../helpers/login';
import { useTranslation } from 'react-i18next';
import { setCanRefresh } from '../../actions/appActions';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import Button from '../../components/Common/Button/Button';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import { Spinner } from '../../components/Common/Spinner/Spinner';
import InputFormBorder from '../../components/Common/InputFormBorder/InputFormBorder';
import RPAndCitySelector from '../../components/Common/RpAndCitySelector/RpAndCitySelector';
import ModalPickerSelector from '../../components/ModalSelector/ModalPickerSelector';
import { restaurantList } from '../../services/restaurantList/restaurantListService';

const SignUpMultiRoleFormScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const phone = getQueryString('phone');
  const token = getQueryString('token');
  const countryCode = getQueryString('countryCode');
  const requiresRPParam = getQueryString('requiresRP') === 'true';

  const welletContext = useContext(WelletContext);
  const dispatch = useDispatch();

  const tenantId = useSelector((state) => state.app.tenantId);
  const marketSetupId = useSelector((state) => state.app?.currentMarket?.marketSetupId);
  const requiresRP =
    useSelector((state) => state.app?.currentMarket?.requiresRP) ?? requiresRPParam;

  const [localErrors, setLocalErrors] = useState({});
  const [verifyingUser, setVerifyingUser] = useState(false);
  const [errorUser, setErrorUser] = useState(undefined);

  const [selectedRp, setSelectedRp] = useState({ label: t('signUp.selectRp') });
  const [selectedCity, setSelectedCity] = useState({ label: t('signUp.selectCity') });
  const [affiliateType, setAffiliateType] = useState(null);

  const [subAffiliateType, setSubAffiliateType] = useState(null);
  const [otherIndependent, setOtherIndependent] = useState('');

  const [selectedWorkUnit, setSelectedWorkUnit] = useState(null);
  const [otherWorkUnit, setOtherWorkUnit] = useState('');

  const [optionCities, setOptionCities] = useState(null);

  useEffect(() => {
    dispatch(setCanRefresh(false));
  }, [dispatch]);

  const validate = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = t('emailError.EMPTY_EMAIL');
    } else if (!values.email.includes('@')) {
      errors.email = t('emailError.EMAIL_@');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    if (!values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMPTY_EMAIL');
    } else if (values.email !== values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMAIL_NOT_MATCH');
    }

    if (!values.firstName) errors.firstName = t('nameError.EMPTY_NAME');
    if (!values.lastName) errors.lastName = t('nameError.EMPTY_LAST_NAME');

    if (!affiliateType) errors.affiliateType = t('affiliateType.error.selectAffiliateType');

    // ──────────────────────────────────────────
    // CONCIERGE
    // ──────────────────────────────────────────
    if (affiliateType === 'CONCIERGE') {
      if (!values.hotelName) errors.hotelName = t('affiliateType.error.emptyHotelName');
      if (!values.city) errors.city = t('selectCity');
      if (requiresRP && !values.rp) errors.rp = t('selectRP');
    }

    // ──────────────────────────────────────────
    // INTERNAL STAFF
    // ──────────────────────────────────────────
    if (affiliateType === 'INTERNAL_STAFF') {
      if (!subAffiliateType)
        errors.subAffiliateType = t('affiliateType.error.selectInternalAffiliateType');

      if (subAffiliateType === 'other' && !otherIndependent)
        errors.otherIndependent = t('pleaseSpecifyActivity');

      if (!selectedWorkUnit) errors.selectedWorkUnit = t('selectYourOperatingUnit');

      if (selectedWorkUnit === 'other' && !otherWorkUnit)
        errors.otherWorkUnit = t('pleaseSpecifyUnitOperation');
    }

    // ──────────────────────────────────────────
    // INDEPENDENT
    // ──────────────────────────────────────────
    if (affiliateType === 'INDEPENDENT') {
      if (!subAffiliateType)
        errors.subAffiliateType = t('affiliateType.error.selectIndependentAffiliateType');

      if (subAffiliateType === 'other' && !otherIndependent)
        errors.otherIndependent = t('pleaseSpecifyActivity');
    }

    return errors;
  };

  const restaurantListService = async () => {
    try {
      const response = await restaurantList(welletContext, marketSetupId);
      const data = Array.isArray(response.data) ? response.data : [];
      const extraOptions = [t('workUnit.corporate'), t('workUnit.other')];

      setOptionCities([...data, ...extraOptions]);
    } catch (error) {
      console.error(error);
    }
  };

  const getTranslatedOrOriginal = (key, fallback, t) => {
    return t(key, { defaultValue: fallback });
  };

  useEffect(() => {
    restaurantListService();
  }, []);

  const sendForm = async () => {
    setVerifyingUser(true);

    try {
      const result = await welletContext.apis.tickets.post(`users/Available`, {
        email: values.email,
        tenantId,
      });

      if (result.data.isAvailable) {
        errorText(result.data.availableMessage, setErrorUser, t);
      } else {
        let query = `?phone=${phone}&countryCode=${countryCode}&email=${encodeURIComponent(
          values.email,
        )}&token=${token}`;

        query += `&firstName=${encodeURIComponent(values.firstName)}`;
        query += `&lastName=${encodeURIComponent(values.lastName)}`;
        query += `&affiliateType=${encodeURIComponent(affiliateType)}`;

        // ──────────────────────────────────────────
        // CONCIERGE
        // ──────────────────────────────────────────
        if (affiliateType === 'CONCIERGE') {
          query += `&hotelName=${encodeURIComponent(values.hotelName)}`;
          query += `&city=${encodeURIComponent(values.city)}`;
          if (requiresRP) query += `&rp=${encodeURIComponent(values.rp)}`;
        }

        // ──────────────────────────────────────────
        // INTERNAL STAFF
        // ──────────────────────────────────────────
        if (affiliateType === 'INTERNAL_STAFF') {
          const finalJobTitleRaw =
            normalizedIndependent === 'other' ? otherIndependent : subAffiliateType;

          const finalWorkUnitRaw = normalized === 'other' ? otherWorkUnit : selectedWorkUnit;

          query += `&jobtitle=${encodeURIComponent(finalJobTitleRaw)}`;
          query += `&workUnit=${encodeURIComponent(finalWorkUnitRaw)}`;
        }

        // ──────────────────────────────────────────
        // INDEPENDENT
        // ──────────────────────────────────────────
        if (affiliateType === 'INDEPENDENT') {
          const finalSectorRaw =
            normalizedIndependent === 'other' ? otherIndependent : subAffiliateType;

          const finalSector = getTranslatedOrOriginal(
            `affiliateType.options.${finalSectorRaw}`,
            finalSectorRaw,
            t,
          );

          query += `&sectorAffiliate=${encodeURIComponent(finalSector)}`;
        }

        query += `&requiresRP=${requiresRP}`;

        history.push({
          pathname: '/sign-up-selfie',
          search: query,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setVerifyingUser(false);
    }
  };

  const normalized = ['otro', 'other'].includes(selectedWorkUnit?.toLowerCase())
    ? 'other'
    : selectedWorkUnit;

  const normalizedIndependent = ['otro', 'other'].includes(subAffiliateType?.toLowerCase())
    ? 'other'
    : subAffiliateType;

  const onChange = (e) => {
    setErrorUser('');
    handleChange(e);
  };

  const { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  useEffect(() => {
    setLocalErrors(errors);
  }, [errors]);

  const affiliateOptions = [
    { value: 'CONCIERGE', label: t('affiliateType.options.CONCIERGE') },
    { value: 'INDEPENDENT', label: t('affiliateType.options.INDEPENDENT') },
    { value: 'INTERNAL_STAFF', label: t('affiliateType.options.INTERNALSTAFF') },
  ];

  const optionKeys = [
    'driverUber',
    'driverDidi',
    'driverTaxi',
    'influencer',
    'contentCreator',
    'youtuber',
    'executiveSecretary',
    'tourPhotographer',
    'receptionist',
    'weddingPlanner',
    'eventOrganizer',
    'tourGuide',
    'independentTourism',
    'hostAirbnb',
    'accommodationOwner',
    'freelancer',
    'other',
  ];

  const independentOptions = optionKeys.map((key) => ({
    value: getTranslatedOrOriginal(`affiliateType.internalStaff.${key}`, key, t),
    label: getTranslatedOrOriginal(`affiliateType.options.${key}`, key, t),
  }));

  const internalStaffKeys = ['salesperson'];

  const internalStaffOptions = internalStaffKeys.map((key) => ({
    value: getTranslatedOrOriginal(`affiliateType.internalStaff.${key}`, key, t),
    label: getTranslatedOrOriginal(`affiliateType.internalStaff.${key}`, key, t),
  }));

  return (
    <>
      <Header />
      <ContentApp background={true}>
        <div>
          <div className='font-medium bold text-white' style={{ lineHeight: '1.6rem' }}>
            {t('yourPersonalInformation')}
          </div>
          <div className='mt-2 text-secondary subtitle' style={{ lineHeight: '1.3rem' }}>
            {t('signUp.subtitle3')}
          </div>

          <form onSubmit={handleSubmit}>
            {/* FIRST NAME */}
            <div className='mt-4'>
              <InputFormBorder
                name='firstName'
                type='text'
                value={values.firstName || ''}
                error={localErrors.firstName}
                label={t('profile.firstName')}
                placeholder={t('profile.firstName')}
                onChange={onChange}
              />
            </div>

            {/* LAST NAME */}
            <div className='mt-4'>
              <InputFormBorder
                name='lastName'
                type='text'
                value={values.lastName || ''}
                error={localErrors.lastName}
                label={t('profile.lastName')}
                placeholder={t('profile.lastName')}
                onChange={onChange}
              />
            </div>

            {/* EMAIL */}
            <div className='mt-4'>
              <InputFormBorder
                name='email'
                type='email'
                value={values.email || ''}
                error={localErrors.email}
                label={t('E-mail')}
                placeholder={t('enterEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            {/* CONFIRM EMAIL */}
            <div className='mt-4'>
              <InputFormBorder
                name='emailConfirmation'
                type='email'
                value={values.emailConfirmation || ''}
                error={localErrors.emailConfirmation}
                label={t('signUp.confirmEmail')}
                placeholder={t('reEnterEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            {/* AFFILIATE TYPE */}
            <div
              className={`field-border-container mt-4 ${localErrors.affiliateType ? 'error' : ''}`}
            >
              <div className='field-none-border p-0'>
                <div className='field-border-name' style={{ zIndex: '2' }}>
                  {t('affiliateType.label.affiliateType')}
                </div>

                <ModalPickerSelector
                  title={t('selectType')}
                  options={affiliateOptions}
                  selectedValue={affiliateOptions.find((o) => o.value === affiliateType) || null}
                  onChange={(option) => {
                    setAffiliateType(option.value);
                    setLocalErrors((p) => ({ ...p, affiliateType: undefined }));
                    setSubAffiliateType(null);
                  }}
                  placeholder={t('selectType')}
                  containerClassName='select-picker'
                  containerStyle={{ opacity: 1, cursor: 'pointer' }}
                />
              </div>
              {localErrors?.affiliateType && (
                <div className='errorLabel'>{localErrors.affiliateType}</div>
              )}
            </div>

            {/* ─────────────────────────────────────────── */}
            {/* CONCIERGE */}
            {/* ─────────────────────────────────────────── */}
            {affiliateType === 'CONCIERGE' && (
              <div className='mt-4'>
                <RPAndCitySelector
                  requiresRP={requiresRP}
                  selectedRp={selectedRp}
                  setSelectedRp={(rp) => {
                    setSelectedRp(rp);
                    setLocalErrors((prev) => ({ ...prev, rp: undefined }));
                  }}
                  selectedCity={selectedCity}
                  setSelectedCity={(city) => {
                    setSelectedCity(city);
                    setLocalErrors((prev) => ({ ...prev, city: undefined }));
                  }}
                  handleChange={onChange}
                  errors={localErrors}
                  values={values}
                />
              </div>
            )}

            {/* ─────────────────────────────────────────── */}
            {/* INTERNAL STAFF */}
            {/* ─────────────────────────────────────────── */}
            {affiliateType === 'INTERNAL_STAFF' && (
              <>
                {/* jobTitle */}
                <div
                  className={`field-border-container mt-4 ${
                    localErrors.subAffiliateType ? 'error' : ''
                  }`}
                >
                  <div className='field-none-border p-0'>
                    <div className='field-border-name' style={{ zIndex: '2' }}>
                      {t('affiliateType.label.internalAffiliateType')}
                    </div>

                    <ModalPickerSelector
                      title={t('affiliateType.label.internalAffiliateType')}
                      options={internalStaffOptions}
                      selectedValue={
                        internalStaffOptions.find((o) => o.value === subAffiliateType) || null
                      }
                      onChange={(option) => {
                        setSubAffiliateType(option.value);
                        setLocalErrors((prev) => ({ ...prev, subAffiliateType: undefined }));
                        if (option.value !== 'other') setOtherIndependent('');
                      }}
                      placeholder={t('affiliateType.label.internalAffiliateType')}
                      containerClassName='select-picker'
                    />
                  </div>
                  {localErrors?.subAffiliateType && (
                    <div className='errorLabel'>{localErrors.subAffiliateType}</div>
                  )}
                </div>

                {normalizedIndependent === 'other' && (
                  <div className='mt-4'>
                    <InputFormBorder
                      name='otherIndependent'
                      type='text'
                      value={otherIndependent}
                      error={localErrors.otherIndependent}
                      label={t('specifyYourActivity')}
                      placeholder={t('describeYourActivity')}
                      onChange={(e) => {
                        setOtherIndependent(e.target.value);
                        setLocalErrors((prev) => ({ ...prev, otherIndependent: undefined }));
                      }}
                    />
                  </div>
                )}

                {/* WorkUnit */}
                {Array.isArray(optionCities) && optionCities.length > 0 && (
                  <div
                    className={`field-border-container mt-4 ${
                      localErrors.selectedWorkUnit ? 'error' : ''
                    }`}
                  >
                    <div className='field-none-border p-0'>
                      <div className='field-border-name' style={{ zIndex: '2' }}>
                        {t('selectYourOperatingUnit')}
                      </div>

                      <ModalPickerSelector
                        title={t('selectYourOperatingUnit')}
                        options={optionCities.map((x) => {
                          const key = x.trim();

                          return {
                            value: key,
                            label: getTranslatedOrOriginal(`workUnit.${key}`, x, t),
                          };
                        })}
                        selectedValue={
                          selectedWorkUnit
                            ? {
                                value: selectedWorkUnit,
                                label: getTranslatedOrOriginal(
                                  `workUnit.${selectedWorkUnit}`,
                                  selectedWorkUnit,
                                  t,
                                ),
                              }
                            : null
                        }
                        onChange={(option) => {
                          setSelectedWorkUnit(option.value);
                          setLocalErrors((prev) => ({
                            ...prev,
                            selectedWorkUnit: undefined,
                          }));
                        }}
                        placeholder={t('selectYourOperatingUnit')}
                        containerClassName='select-picker'
                      />
                    </div>

                    {localErrors?.selectedWorkUnit && (
                      <div className='errorLabel'>{localErrors.selectedWorkUnit}</div>
                    )}
                  </div>
                )}

                {normalized === 'other' && (
                  <div className='mt-4'>
                    <InputFormBorder
                      name='otherWorkUnit'
                      type='text'
                      value={otherWorkUnit}
                      error={localErrors.otherWorkUnit}
                      label={t('specifyYourUnitOperation')}
                      placeholder={t('enteryourUnit')}
                      onChange={(e) => {
                        setOtherWorkUnit(e.target.value);
                        setLocalErrors((prev) => ({ ...prev, otherWorkUnit: undefined }));
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {/* ─────────────────────────────────────────── */}
            {/* INDEPENDENT */}
            {/* ─────────────────────────────────────────── */}
            {affiliateType === 'INDEPENDENT' && (
              <>
                <div
                  className={`field-border-container mt-4 ${
                    localErrors.subAffiliateType ? 'error' : ''
                  }`}
                >
                  <div className='field-none-border p-0'>
                    <div className='field-border-name' style={{ zIndex: '2' }}>
                      {t('affiliateType.label.independentAffiliateType')}
                    </div>

                    <ModalPickerSelector
                      title={t('selectType')}
                      options={independentOptions}
                      selectedValue={
                        independentOptions.find((o) => o.value === subAffiliateType) || null
                      }
                      onChange={(option) => {
                        setSubAffiliateType(option.value);
                        setLocalErrors((prev) => ({ ...prev, subAffiliateType: undefined }));
                        if (option.value !== 'other') setOtherIndependent('');
                      }}
                      placeholder={t('selectType')}
                      containerClassName='select-picker'
                    />
                  </div>
                  {localErrors?.subAffiliateType && (
                    <div className='errorLabel'>{localErrors.subAffiliateType}</div>
                  )}
                </div>

                {normalizedIndependent === 'other' && (
                  <div className='mt-4'>
                    <InputFormBorder
                      name='otherIndependent'
                      type='text'
                      value={otherIndependent}
                      error={localErrors.otherIndependent}
                      label={t('specifyYourActivity')}
                      placeholder={t('describeYourActivity')}
                      onChange={(e) => {
                        setOtherIndependent(e.target.value);
                        setLocalErrors((prev) => ({ ...prev, otherIndependent: undefined }));
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {errorUser && (
              <div className='mt-5'>
                <ErrorBox title={errorUser} />
              </div>
            )}

            <div className='mt-4 font-tiny text-secondary'>{t('signUp.confirmText')}</div>

            <div className='mt-4'>
              <Button
                size='large'
                fontSize='18px'
                text={
                  verifyingUser ? (
                    <div className='d-flex justify-content-center'>
                      <Spinner size={25} color='#000' />
                    </div>
                  ) : (
                    t('continue')
                  )
                }
                icon={!verifyingUser}
                typeIcon='arrow'
                iconColor='#000'
              />
            </div>
          </form>
        </div>
      </ContentApp>
    </>
  );
};

export default SignUpMultiRoleFormScreen;
